import {
  type FC,
  type MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  useApolloClient,
  useMutation,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, CircularProgress, List } from "@mui/material";
import { useTranslation } from "@/shared/hooks";
import {
  ChatMessagesQuery,
  ChatMessageCreatedSubscription,
  ChatMessageDeletedSubscription,
  DeleteChatMessageMutation,
  CreateChatBanMutation,
} from "@/entities/contents";
import type {
  ChatMessagesQueryType,
  ChatMessageCreatedSubscriptionType,
  ChatMessageDeletedSubscriptionType,
  DeleteChatMessageMutationType,
  CreateChatBanMutationType,
} from "@/types/graphql/site.generated";
import { ChatMessageListItem } from "./ChatMessageListItem";
import { filterVisibleChatMessages } from "./filterVisibleChatMessages";
import { orderChatMessages } from "./orderChatMessages";
import { restoreScrollAfterPrepend } from "./restoreScrollAfterPrepend";
import type { ContentChatMode } from "./ContentChat";
import type { ChatMessageListItemType } from "./types";

const PAGE_SIZE = 30;

// Как часто пересчитывать видимость join/left-строк (filterVisibleChatMessages
// сравнивает их createdAt с этим "now"): раз в 30с достаточно, чтобы строка
// пропала из открытого чата в течение своего 2-минутного окна, не мигая на
// каждый ререндер.
const VISIBILITY_TICK_MS = 30_000;

// Сколько пикселей от низа ленты ещё считается «зритель внизу»: пока он
// там, новый комментарий подтягивает скролл за собой, а если он ушёл
// читать старое, лента под ним не дёргается.
const STICK_TO_BOTTOM_PX = 48;

// useLayoutEffect ругается на сервере, а компенсация скролла нужна только в
// браузере: на SSR подменяем его обычным эффектом, который всё равно не
// выполнится.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

interface ChatMessageListProps {
  chatRoomId: string;
  mode: ContentChatMode;
  viewerIsModerator: boolean;
  onReply: (message: ChatMessageListItemType) => void;
  onMention: (mention: { userId: string; displayName: string }) => void;
  // Общий с формой отправки флаг «держать низ ленты»: см. комментарий в
  // ContentChat, где он и создаётся.
  stickToBottomRef: MutableRefObject<boolean>;
}

export const ChatMessageList: FC<ChatMessageListProps> = ({
  chatRoomId,
  mode,
  viewerIsModerator,
  onReply,
  onMention,
  stickToBottomRef,
}) => {
  const isStream = mode === "stream";
  const client = useApolloClient();
  const [count, setCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const listRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ChatMessageListItemType[]>([]);

  // Обработчики подписок и догрузки живут дольше одного рендера, а нужна им
  // всегда свежая лента (самое старое сообщение для beforeId, проверка
  // «удалили то, что мы уже показали»), поэтому состояние дублируется в ref.
  const messagesRef = useRef<ChatMessageListItemType[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const initialScrolledRef = useRef(false);

  // Высота ленты, снятая перед вставкой старых комментариев наверх:
  // layout-эффект ниже по ней вернёт точку чтения на место.
  const prependAnchorRef = useRef<number | null>(null);

  const welcomeStorageKey = `chat-welcome-dismissed-${chatRoomId}`;
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    setShowWelcome(!sessionStorage.getItem(welcomeStorageKey));
  }, [welcomeStorageKey]);

  // "<имя> зашёл в чат" тускнеет само через пару минут (filterVisibleChat
  // Messages), а не только на приходе нового сообщения: без тика открытый и
  // застывший чат держал бы такие строки до следующего ререндера.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), VISIBILITY_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const { data, loading } = useQuery<ChatMessagesQueryType>(ChatMessagesQuery, {
    variables: { chatRoomId, limit: PAGE_SIZE },
  });

  useEffect(() => {
    if (data?.chatMessages) {
      setMessages(data.chatMessages.nodes as ChatMessageListItemType[]);
      setCount(data.chatMessages.count);
    }
  }, [data]);

  // Догрузка страницы вверх идёт через client.query, а не fetchMore:
  // лента живёт в локальном состоянии (её правят подписки), в кэше для
  // chatMessages нет merge-политики, и fetchMore просто перетёр бы
  // первую страницу.
  const handleLoadMore = async () => {
    if (loadingMore) return;

    const loaded = messagesRef.current;
    const oldest = loaded[loaded.length - 1];
    if (!oldest) return;

    setLoadingMore(true);
    try {
      const result = await client.query<ChatMessagesQueryType>({
        query: ChatMessagesQuery,
        variables: {
          chatRoomId,
          beforeId: String(oldest.id),
          limit: PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      });

      const older = (result.data?.chatMessages?.nodes ??
        []) as ChatMessageListItemType[];
      if (older.length === 0) return;

      prependAnchorRef.current = listRef.current?.scrollHeight ?? null;
      setMessages((prev) => [...prev, ...older]);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : String(error), {
        variant: "error",
      });
    } finally {
      setLoadingMore(false);
    }
  };

  useSubscription<ChatMessageCreatedSubscriptionType>(
    ChatMessageCreatedSubscription,
    {
      variables: { chatRoomId },
      onData: ({ data: subData }) => {
        const created = subData.data?.chatMessageCreated;
        if (!created) return;
        setMessages((prev) => [created as ChatMessageListItemType, ...prev]);
        setCount((prev) => prev + 1);
        // Чат эфира читается сверху вниз (новое первым): прыжок наверх не
        // зависит от высоты ленты и делается сразу. Под видео новое внизу,
        // и доскроллить можно только после рендера, этим занят эффект ниже.
        if (isStream) listRef.current?.scrollTo({ top: 0 });
      },
    },
  );

  useSubscription<ChatMessageDeletedSubscriptionType>(
    ChatMessageDeletedSubscription,
    {
      variables: { chatRoomId },
      onData: ({ data: subData }) => {
        const deletedId = subData.data?.chatMessageDeleted?.id;
        if (!deletedId) return;
        // Счётчик кормит кнопку «показать ещё»: не уменьшив его на удалении
        // уже загруженного сообщения, мы навсегда оставили бы hasMore
        // истинным и кнопку, которая ничего не догружает.
        const wasLoaded = messagesRef.current.some((m) => m.id === deletedId);
        setMessages((prev) => prev.filter((m) => m.id !== deletedId));
        if (wasLoaded) setCount((prev) => Math.max(0, prev - 1));
      },
    },
  );

  // Компенсация скролла после «показать ещё»: под видео старые комментарии
  // встают НАД лентой, и без сдвига scrollTop на прирост высоты зритель
  // уехал бы к самым старым сообщениям. Layout-эффект, а не обычный: сдвиг
  // должен случиться до отрисовки кадра, иначе лента дёргается.
  useIsomorphicLayoutEffect(() => {
    const saved = prependAnchorRef.current;
    if (saved === null) return;
    prependAnchorRef.current = null;

    const node = listRef.current;
    if (!node) return;
    restoreScrollAfterPrepend(node, saved);
  }, [messages]);

  // Комментарии открываются на свежих, а они внизу: первый доскролл делаем
  // без анимации сразу после первой страницы. Дальше низ держим, только
  // если зритель и так там, иначе новое сообщение сбивало бы чтение старых.
  useEffect(() => {
    if (isStream) return;

    const node = listRef.current;
    if (!node || messages.length === 0) return;

    if (!initialScrolledRef.current) {
      node.scrollTo({ top: node.scrollHeight, behavior: "auto" });
      initialScrolledRef.current = true;
      return;
    }

    if (stickToBottomRef.current) {
      node.scrollTo({ top: node.scrollHeight });
    }
  }, [isStream, messages, stickToBottomRef]);

  const handleScroll = () => {
    const node = listRef.current;
    if (!node) return;
    stickToBottomRef.current =
      node.scrollHeight - node.scrollTop - node.clientHeight <
      STICK_TO_BOTTOM_PX;
  };

  const [deleteChatMessage] = useMutation<DeleteChatMessageMutationType>(
    DeleteChatMessageMutation,
    {
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    },
  );
  const [createChatBan] = useMutation<CreateChatBanMutationType>(
    CreateChatBanMutation,
    {
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    },
  );

  const handleDelete = (id: string) => {
    void deleteChatMessage({ variables: { id } });
  };

  const handleBan = (messageId: string, type: "TEMPORARY" | "PERMANENT") => {
    void createChatBan({ variables: { input: { messageId, type } } });
  };

  const dismissWelcome = () => {
    sessionStorage.setItem(welcomeStorageKey, "1");
    setShowWelcome(false);
  };

  const ordered = orderChatMessages(messages, mode);
  // hasMore/count намеренно считаются от messages (пагинация от сервера), а
  // не от visible: скрытые join/left-строки истории не занимают.
  const visible = filterVisibleChatMessages(ordered, now);
  const hasMore = messages.length < count;

  return (
    <Box
      ref={listRef}
      onScroll={isStream ? undefined : handleScroll}
      sx={{
        height: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        overscrollBehavior: "contain",
        scrollBehavior: "smooth",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {isStream && showWelcome && (
        <Alert severity="info" onClose={dismissWelcome} sx={{ m: 1 }}>
          {t("stream.chat.welcomeMessage")}
        </Alert>
      )}
      {!isStream && hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Button
            size="small"
            disabled={loadingMore}
            startIcon={
              loadingMore ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            onClick={() => void handleLoadMore()}
          >
            {t("contentChat.loadMore")}
          </Button>
        </Box>
      )}
      {/* Пустое состояние только после ответа сервера: иначе «комментариев
          пока нет» моргает на каждой первой загрузке. */}
      {!isStream && !loading && messages.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center", opacity: 0.7 }}>
          {t("contentChat.empty")}
        </Box>
      )}
      <List dense disablePadding>
        {visible.map((message) => (
          <ChatMessageListItem
            key={String(message.id)}
            message={message}
            viewerIsModerator={viewerIsModerator}
            onReply={onReply}
            onMention={onMention}
            onDelete={handleDelete}
            onBan={handleBan}
          />
        ))}
      </List>
    </Box>
  );
};
